import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

export const CartDrawer = () => {
  const { items, total, isDrawerOpen, setDrawerOpen, updateQuantity, removeItem } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[55]"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-[#050505] border-l border-white/10 z-[60] shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <div className="flex items-center gap-3">
                <ShoppingBag size={20} className="text-[#B026FF]" />
                <h2 className="font-heading font-bold text-xl">Καλάθι</h2>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-full hover:bg-white/5"
                data-testid="cart-drawer-close"
                aria-label="Κλείσιμο καλαθιού"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4" data-testid="cart-drawer-items">
              {items.length === 0 && (
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 text-zinc-500 py-12">
                  <ShoppingBag size={40} strokeWidth={1} />
                  <div className="font-heading text-lg text-zinc-300">Το καλάθι σας είναι άδειο</div>
                  <p className="text-sm">Προσθέστε τις αγαπημένες σας neon επιγραφές.</p>
                </div>
              )}

              {items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-3 p-3 border border-white/5 rounded-xl"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg bg-black" />
                  <div className="flex-1 flex flex-col gap-2 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium truncate">{item.name}</div>
                      <button
                        onClick={() => removeItem(item.product_id)}
                        className="text-zinc-500 hover:text-[#B026FF]"
                        data-testid={`cart-remove-${item.product_id}`}
                        aria-label="Αφαίρεση"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="text-[#00FFFF] font-semibold text-sm">
                      {(item.price * item.quantity).toFixed(2)}€
                    </div>
                    <div className="flex items-center gap-2 mt-auto">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center rounded border border-white/10 hover:border-[#B026FF]"
                        data-testid={`cart-qty-minus-${item.product_id}`}
                        aria-label="Μείωση"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-sm" data-testid={`cart-qty-${item.product_id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded border border-white/10 hover:border-[#B026FF]"
                        data-testid={`cart-qty-plus-${item.product_id}`}
                        aria-label="Αύξηση"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t border-white/5 flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400 text-sm">Σύνολο</span>
                  <span className="font-heading font-bold text-2xl text-[#00FFFF]" data-testid="cart-total">
                    {total.toFixed(2)}€
                  </span>
                </div>
                <button
                  onClick={goCheckout}
                  className="w-full bg-[#B026FF] text-black font-bold py-4 rounded-lg hover:neon-box-glow-pink transition-all duration-300"
                  data-testid="cart-checkout-button"
                >
                  Ολοκλήρωση παραγγελίας
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
